import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';
import { cx } from '../utils/cx.js';
import styles from './Input.module.css';

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string;
  hint?: string;
  error?: string;
  showErrorText?: boolean;
  /** Show `n / max` when `maxLength` is set. Default true. */
  showCount?: boolean;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    label,
    hint,
    error,
    className,
    id,
    showErrorText = true,
    showCount,
    required,
    maxLength,
    value,
    defaultValue,
    'aria-describedby': ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? rest.name ?? autoId;
  const errorId = `${inputId}-error`;
  const countId = `${inputId}-count`;
  const max =
    typeof maxLength === 'number' && Number.isFinite(maxLength) && maxLength > 0
      ? Math.floor(maxLength)
      : null;
  const countEnabled = showCount ?? max != null;
  const currentLength = (() => {
    if (typeof value === 'string' || typeof value === 'number') return String(value).length;
    if (typeof defaultValue === 'string' || typeof defaultValue === 'number') {
      return String(defaultValue).length;
    }
    return 0;
  })();
  const describedBy =
    [ariaDescribedBy, error ? errorId : null, countEnabled && max != null ? countId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div className={cx(styles.wrap, className)}>
      {label ? (
        <label className={styles.label} htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      <textarea
        ref={ref}
        id={inputId}
        className={cx(styles.input, error && styles.error)}
        style={{ minHeight: 96, resize: 'vertical' }}
        aria-invalid={!!error}
        aria-required={required || undefined}
        aria-describedby={describedBy}
        required={required}
        maxLength={maxLength}
        value={value}
        defaultValue={defaultValue}
        {...rest}
      />
      {showErrorText && error ? (
        <div className={styles.metaRow}>
          <span id={errorId} className={styles.errText} role="alert">
            {error}
          </span>
          {countEnabled && max != null ? (
            <span id={countId} className={styles.charCount} aria-live="polite">
              {currentLength} / {max}
            </span>
          ) : null}
        </div>
      ) : (countEnabled && max != null) || hint ? (
        <div className={styles.metaRow}>
          {hint ? <span className={styles.hint}>{hint}</span> : <span />}
          {countEnabled && max != null ? (
            <span id={countId} className={styles.charCount} aria-live="polite">
              {currentLength} / {max}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
});
